import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Wine, Plus, Search, Filter } from 'lucide-react';
import toast from 'react-hot-toast';

export default function DrinksPage() {
  const navigate = useNavigate();
  const [drinks, setDrinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoria, setSelectedCategoria] = useState('todos');

  const categorias = [
    { value: 'todos', label: 'Todos' },
    { value: 'classicos', label: 'Clássicos' },
    { value: 'caipirinhas', label: 'Caipirinhas' },
    { value: 'martinis', label: 'Martinis' },
    { value: 'tropicais', label: 'Tropicais' },
    { value: 'shots', label: 'Shots' },
    { value: 'semalcool', label: 'Sem Álcool' }
  ];

  useEffect(() => {
    fetchDrinks();
  }, []);

  const fetchDrinks = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('drinks')
        .select('*')
        .order('name');

      if (error) throw error;
      setDrinks(data || []);
    } catch (err) {
      console.error('Erro ao buscar drinks:', err);
      toast.error('Erro ao carregar drinks');
    } finally {
      setLoading(false);
    }
  };

  const filteredDrinks = drinks.filter(drink => {
    const matchesSearch = drink.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategoria = selectedCategoria === 'todos' || drink.category === selectedCategoria;
    return matchesSearch && matchesCategoria;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Drinks Populares</h1>
              <p className="text-gray-600 mt-1">Gerencie seu catálogo de drinks</p>
            </div>
            <button
              onClick={() => navigate('/drinks/novo')}
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Cadastrar Drink
            </button>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Busca */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Buscar drinks..."
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Categoria */}
            <div className="flex items-center gap-2">
              <Filter className="text-gray-400 w-5 h-5" />
              <select
                className="border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                value={selectedCategoria}
                onChange={(e) => setSelectedCategoria(e.target.value)}
              >
                {categorias.map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Lista de Drinks */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Carregando drinks...</p>
          </div>
        ) : filteredDrinks.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredDrinks.map((drink) => (
              <div key={drink.id} className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{drink.name}</h3>
                      <p className="text-sm text-gray-600">{drink.category}</p>
                    </div>
                    <Wine className="w-6 h-6 text-purple-600" />
                  </div>
                  
                  <p className="text-gray-700 text-sm mb-4 line-clamp-2">{drink.description}</p>
                  
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">
                      <span className="font-medium">Copo:</span> {drink.glass_type || 'Padrão'}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      drink.alcohol_level === 'none' ? 'bg-green-100 text-green-800' :
                      drink.alcohol_level === 'low' ? 'bg-blue-100 text-blue-800' :
                      drink.alcohol_level === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {drink.alcohol_level === 'none' ? 'Sem álcool' : 
                       drink.alcohol_level === 'low' ? 'Teor baixo' :
                       drink.alcohol_level === 'medium' ? 'Teor médio' :
                       'Teor alto'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-lg">
            <Wine className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">
              {searchTerm || selectedCategoria !== 'todos' 
                ? 'Nenhum drink encontrado com os filtros aplicados' 
                : 'Nenhum drink cadastrado ainda'}
            </p>
            <button
              onClick={() => navigate('/drinks/novo')}
              className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg transition-colors"
            >
              Cadastrar Primeiro Drink
            </button>
          </div>
        )}
      </div>
    </div>
  );
}